import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { EscalationsServices } from '../../Apis/EscalationsServices';
import { toLowerCase } from '../../constants/commonFunction';
import {
  fetchEscalations,
  getEscalationsState,
} from '../../features/escalationsSlice';
import { useDrawer } from '../../hooks/useDrawer';
import {
  EscalationStatusType,
  FilterProps,
} from '../../pages/Escalations/Escalations';
import { useClientAuth } from '../../providers/ClientProvider';
import { useSnackbar } from '../../providers/SnackbarProvider';
import { useAppDispatch, useAppSelector } from '../../store';
import { EscalationsDto } from '../../types/escalationsType';
import {
  EscalationStatus,
  EscalationType,
  UpadteEscalationRequest,
} from '../../types/request/escalationsRequest';
import { getErrorMessageFromErrorObj } from '../../utils/api';
import LmFilterSection from '../common/LmFilterSection';
import VegaApproveDialog from '../common/VegaApproveDialog';
import VegaDataGrid from '../common/VegaDataGrid';
import VegaPageContent from '../common/VegaPageContent';
import VegaPageHeader from '../common/VegaPageHeader';
import VegaTabBar from '../common/VegaTabBar';
import VegaTabBarItem from '../common/VegaTabItem';
import VegaText from '../common/VegaText';
import { MinVisitsColumn } from './Columns/MinVisitsColumn';
import EscalationFilterDrawer from './Drawer/EscalationFilterDrawer';
import { getUserInfo } from './EscalationCommonFunctions';

const ListOfMinimumVisits = () => {
  const { loading, error, escalations, totalItems } =
    useAppSelector(getEscalationsState);
  const dispatch = useAppDispatch();
  const { user } = useClientAuth();

  const { setSnackbar } = useSnackbar();
  const [userName, setUserName] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [escalationData, setEscalationData] = useState<Partial<EscalationsDto>>(
    {}
  );
  const [formData, setFormData] = useState<FilterProps>({
    fromDate: '',
    toDate: '',
  });

  const {
    open: onApproveOpen,
    close: onApproveClose,
    isOpen: isApproveOpen,
  } = useDrawer();

  const {
    open: onOpenFilterDrawer,
    close: onCloseFilterDrawer,
    isOpen: isOpenFilterDrawer,
  } = useDrawer();

  const onReolveDialogOpen = async (escalation: EscalationsDto) => {
    onApproveOpen();
    setEscalationData(escalation);
    const userInfo = await getUserInfo(escalation.userId);
    setUserName(userInfo);
  };

  const updateEscalation = (
    escalationStatus: EscalationStatus,
    rejectedReason: string,
    onClose: () => void
  ) => {
    const request = {
      id: escalationData.id,
      escalationStatus,
      ...(rejectedReason.length && { rejectedReason }),
    } as UpadteEscalationRequest;

    EscalationsServices.updateEscalation(request)
      .then(() => {
        setSnackbar(`${escalationStatus} successfully !!!`);
        onClose();
        getEscalationList();
      })
      .catch(error => {
        setSnackbar(getErrorMessageFromErrorObj(error), 'error');
        onClose();
      });
  };

  const onClear = () => {
    setFormData({
      fromDate: '',
      toDate: '',
    });
    onCloseFilterDrawer();
    dispatch(
      fetchEscalations({
        page,
        size,
        managerId: user?.id,
        escalationType: EscalationType.MINIMUM_VISIT_UNFULFILLED,
        ...(search.length && { userName: search }),
        ...(selectedStatus === 0
          ? {
              escalationStatus: [EscalationStatus.RAISED],
            }
          : {
              escalationStatus: [
                EscalationStatus.RESOLVED,
                EscalationStatus.REJECTED,
                EscalationStatus.ESCALATED,
              ],
            }),
      })
    );
  };

  const getEscalationList = () => {
    dispatch(
      fetchEscalations({
        page,
        size,
        managerId: user?.id,
        escalationType: EscalationType.MINIMUM_VISIT_UNFULFILLED,
        ...(search.length && { userName: search }),
        ...(formData.fromDate.length && { fromDate: formData.fromDate }),
        ...(formData.toDate.length && { toDate: formData.toDate }),
        ...(selectedStatus === 0
          ? {
              escalationStatus: [EscalationStatus.RAISED],
            }
          : {
              escalationStatus: [
                EscalationStatus.RESOLVED,
                EscalationStatus.REJECTED,
                EscalationStatus.ESCALATED,
              ],
            }),
      })
    );
  };

  useEffect(() => {
    if (user) {
      if (search.length) {
        const getEscalation = setTimeout(getEscalationList, 500);
        return () => clearTimeout(getEscalation);
      } else {
        getEscalationList();
      }
    }
  }, [page, size, selectedStatus, search, user]);

  useEffect(() => {
    if (error) {
      setSnackbar(getErrorMessageFromErrorObj(error), 'error');
    }
  }, [error]);
  return (
    <>
      <VegaPageHeader
        sx={{
          pb: 0,
          bgcolor: 'transparent',
          borderBottom: 'none',
        }}
        renderLeftView={() => {
          return (
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <VegaTabBar
                value={selectedStatus}
                variant="scrollable"
                scrollButtons={false}
                onChange={(e, selected) => {
                  setSelectedStatus(selected);
                }}
              >
                {Object.keys(EscalationStatusType).map((type: string) => (
                  <VegaTabBarItem key={type} label={toLowerCase(type)} />
                ))}
              </VegaTabBar>
            </Stack>
          );
        }}
        renderRightView={() => {
          return (
            <LmFilterSection
              setSearch={setSearch}
              search={search}
              onFilter={onOpenFilterDrawer}
            />
          );
        }}
      />
      <VegaPageContent mt={2}>
        <VegaDataGrid
          idColumn="id"
          data={escalations}
          columns={MinVisitsColumn({
            selectedTab: selectedStatus,
            onReolveDialogOpen,
          })}
          loading={loading}
          page={page}
          pageSize={size}
          rowCount={totalItems}
          onPageChange={page => setPage(page)}
          onPageSizeChange={size => setSize(size)}
        />
      </VegaPageContent>
      <VegaApproveDialog
        open={isApproveOpen}
        onClose={onApproveClose}
        onApprove={() =>
          updateEscalation(EscalationStatus.RESOLVED, '', onApproveClose)
        }
        header="Resolve Minimum Visits?"
        buttonText="Resolve"
      >
        <VegaText
          text={`You are resolving request of agent ${userName} `}
          fontWeight={600}
          fontSize={'1.0625rem'}
          color={'black'}
        />
      </VegaApproveDialog>
      <EscalationFilterDrawer
        open={isOpenFilterDrawer}
        onClose={onCloseFilterDrawer}
        formData={formData}
        setFormData={setFormData}
        onFilter={getEscalationList}
        onClear={onClear}
      />
    </>
  );
};

export default ListOfMinimumVisits;
